"use client";

import { useEffect } from "react";

import { useMarketingAnalytics } from "@/hooks/useMarketingAnalytics";

import type { RewindShot } from "./home-rewind.types";

type HomeRewindEnhancerProps = {
  shot: RewindShot;
};

/**
 * Loads the scroll engine after hydration so GSAP and Lenis never block first
 * paint. Renders nothing; the server markup is already the full hero.
 */
export const HomeRewindEnhancer = ({ shot }: HomeRewindEnhancerProps) => {
  const { track } = useMarketingAnalytics();

  useEffect(() => {
    const root = document.querySelector<HTMLElement>("[data-rewind-root]");
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let stop: (() => void) | undefined;
    let cancelled = false;
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      void import("./rewind-engine").then(({ startRewind }) => {
        if (cancelled) return;
        stop = startRewind({
          root,
          shot,
          onEvent: (name, properties) => track({ name, properties }),
        });
      });
    };

    // Start after load and an idle slot, or on the first scroll, whichever comes first.
    let idleHandle = 0;
    const startSoon = () => {
      window.removeEventListener("load", startSoon);
      window.removeEventListener("scroll", startNow);
      idleHandle = window.requestIdleCallback
        ? window.requestIdleCallback(start, { timeout: 1500 })
        : window.setTimeout(start, 200);
    };
    const startNow = () => {
      window.removeEventListener("load", startSoon);
      window.removeEventListener("scroll", startNow);
      start();
    };
    if (document.readyState === "complete") startSoon();
    else window.addEventListener("load", startSoon);
    window.addEventListener("scroll", startNow, { passive: true, once: true });

    return () => {
      cancelled = true;
      window.removeEventListener("load", startSoon);
      window.removeEventListener("scroll", startNow);
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleHandle);
      else window.clearTimeout(idleHandle);
      stop?.();
    };
  }, [shot, track]);

  return null;
};
